/**
 * FullStacked Cloud Console - Boot sequence: load /v1/config, establish the SSO session
 * (exchange token redemption or sessionStorage) and mount the console shell.
 */

import { useEffect, useState } from "react";
import { exchangeToken } from "./api/auth.ts";
import { errorMessage, type ApiContext } from "./api/client.ts";
import { loadPublicConfig } from "./api/platform.ts";
import type { PublicConfig, SessionPayload } from "./api/types.ts";
import {
    cleanExchangeTokenFromUrl,
    clearSession,
    extractExchangeToken,
    loadSession,
    redirectToAuth,
    saveSession,
    takeCodeVerifier,
} from "./auth-redirect.ts";
import { ConsoleShell } from "./shell/shell.tsx";

type BootState =
    | { phase: "starting"; message: string }
    | { phase: "error"; message: string; config?: PublicConfig }
    | { phase: "ready"; config: PublicConfig; session: SessionPayload };

async function establishSession(config: PublicConfig): Promise<SessionPayload | null> {
    const exchange = extractExchangeToken(window.location.href);
    if (exchange) {
        const ctx: ApiContext = {
            services: config.services,
            getToken: () => null,
            onUnauthorized: () => {},
        };
        try {
            const verifier = takeCodeVerifier(window.sessionStorage);
            if (!verifier)
                throw new Error(
                    "This sign-in was started in another tab or browser session. Sign in again."
                );
            const session = await exchangeToken(ctx, exchange, verifier);
            saveSession(window.sessionStorage, session);
            return session;
        } finally {
            cleanExchangeTokenFromUrl();
        }
    }
    return loadSession(window.sessionStorage);
}

export function Boot() {
    const [state, setState] = useState<BootState>({
        phase: "starting",
        message: "Loading configuration",
    });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const loaded = await loadPublicConfig();
            const config = {
                ...loaded,
                services: { ...loaded.services, console: window.location.origin },
            };
            if (!cancelled) setState({ phase: "starting", message: "Establishing session" });
            try {
                const session = await establishSession(config);
                if (cancelled) return;
                if (!session) {
                    setState({
                        phase: "starting",
                        message: "Redirecting to FullStacked Cloud sign in",
                    });
                    await redirectToAuth(config.services.auth);
                    return;
                }
                setState({ phase: "ready", config, session });
            } catch (err) {
                if (!cancelled) setState({ phase: "error", message: errorMessage(err), config });
            }
        })().catch((err: unknown) => {
            if (!cancelled) setState({ phase: "error", message: errorMessage(err) });
        });
        return () => {
            cancelled = true;
        };
    }, []);

    if (state.phase === "ready") {
        const signOutLocal = (): void => {
            clearSession(window.sessionStorage);
            void redirectToAuth(state.config.services.auth);
        };
        return (
            <ConsoleShell
                config={state.config}
                session={state.session}
                onSessionEnded={signOutLocal}
            />
        );
    }

    return (
        <div
            className="console-container"
            style={{ alignItems: "center", justifyContent: "center" }}
        >
            <div className="card" style={{ width: 420, maxWidth: "calc(100% - 32px)" }}>
                <div className="row" style={{ marginBottom: 12 }}>
                    <span className="brand-text">FullStacked Cloud Console</span>
                </div>
                {state.phase === "starting" ? (
                    <div className="row muted">
                        <span className="spinner" />
                        <span>{state.message}</span>
                    </div>
                ) : (
                    <div className="stack">
                        <div className="state-box error" role="alert">
                            {state.message}
                        </div>
                        {state.config ? (
                            <button
                                className="btn btn-primary"
                                onClick={() =>
                                    state.config && void redirectToAuth(state.config.services.auth)
                                }
                            >
                                Sign in again
                            </button>
                        ) : (
                            <button
                                className="btn btn-secondary"
                                onClick={() => window.location.reload()}
                            >
                                Reload
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
